import {toast} from 'react-toastify'

import ToastMessage from '../components/Toast'
import {CheckIcon, CrossIcon} from '../icons'

/**
 * JsonResult - Frontend component for handling standardized JSON API responses
 *
 * This component processes responses from APIs that use the JsonResultConcern pattern,
 * providing automatic error handling, form field validation, and toast notifications.
 *
 * ## Usage with API Responses
 *
 *   import {onJsonResponse} from '~/react/hooks/jsonResult'
 *
 *   const handleSubmit = async (values, {setFieldError}) => {
 *     const result = await apiCall(values)
 *
 *     const onSuccess = (showMessage) => {
 *       // Handle successful response
 *       onItemCreated(result.savedItem)
 *       showMessage('Successfully created!')
 *       closeForm()
 *     }
 *
 *     const onFailure = (fieldKey, error, value) => {
 *       // Set field-specific errors in form
 *       setFieldError(fieldKey, translateError(fieldKey, error, value))
 *     }
 *
 *     onJsonResponse(result, {onSuccess, onFailure})
 *   }
 *
 * ## Expected Response Format
 *
 * Success responses:
 *   { success: true, ...data }
 *
 * Error responses:
 *   { success: false, errors: { field: [{error: "type", value: "invalid"}] } }
 *
 * ## Automatic Behavior
 *
 * - Success: Calls onSuccess callback with toast function
 * - Field Errors: Calls onFailure for each field error
 * - Base Errors: Shows toast notifications automatically
 * - Toast Integration: Uses react-toastify with styled messages
 *
 * ## Backend Integration
 *
 * This component is designed to work with the JsonResultConcern Rails pattern.
 * See app/controllers/concerns/json_result_concern.rb for backend implementation details.
 */

const onToast = (message, icon) => {
  toast.dark(
    <ToastMessage>
      {icon}
      {message}
    </ToastMessage>,
  )
}

const onSuccessToast = (message) => {
  onToast(message, <CheckIcon mr={1} color="mld.success.700" />)
}

const onFailureToast = (message) => {
  onToast(message, <CrossIcon mr={1} color="mld.alert.700" />)
}

export const onJsonResponse = (result, {onSuccess, onFailure}) => {
  if (result.success) {
    if (onSuccess) {
      onSuccess(onSuccessToast)
    }
    return
  }

  Object.entries(result.errors).forEach(([key, errors]) => {
    if (key === 'base') {
      onFailureToast(errors.map((error) => error.error).join(', '))
      return
    }

    if (onFailure) {
      errors.forEach(({error, value}) => {
        onFailure(key, error, value)
      })
    }
  })
}
